import * as React from "react";
import {useEffect, useState} from "react";
import {Box, Collapse, Grid2} from "@mui/material";
import {ChangeHandler} from "@/lib/modelstate";
import {UserSignUpForm} from "@/pages/company-sign-up-page/steps/UserSignUpForm";
import {CompanyForm} from "@/pages/company-sign-up-page/steps/CompanyForm";
import Agreement, {AgreementLoadState} from "@/pages/company-sign-up-page/steps/AgreementForm";
import {CompanySignUpDTO, LegalDocument} from "@/api/data-contracts";
import {BankDetailsForm} from "@/pages/company-sign-up-page/steps/BankDetailsForm";
import {CompanyDetailsForm} from "@/pages/company-sign-up-page/steps/CompanyDetailsForm";
import {isEmpty} from "@/lib/accessor";
import {ErrorsShape} from "@/lib/utils";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {useTranslation} from "react-i18next";
import {AuthI18N} from "@/translations";
import {useSnackbarEx} from "@/lib/snackbarex";
import {fetchLegalDocumentContent, LegalDocumentContent} from "@/common/legal/legalDocuments";
import {resolveApiLanguage} from "@/app/locale/anonymousLanguage";

/** One agreement step's text together with the state of its load request. */
interface AgreementState {
    agreement?: LegalDocumentContent;
    loadState: AgreementLoadState;
}

const LOADING: AgreementState = {loadState: "loading"};

export const CompanySignUpForm = ({
    model,
    errors,
    onChange,
    onSubmit,
    activeStep,
    setActiveStep,
                                      steps
}: {
    model?: CompanySignUpDTO;
    onChange: ChangeHandler;
    errors: ErrorsShape<CompanySignUpDTO>;
    onSubmit?: () => void;
    activeStep: number;
    setActiveStep: (arg: (step: number) => number) => void;
    steps: string[];
}) => {
    // When the user is already confirmed, the user data step (index 0) is skipped,
    // so all step indices are shifted by 1 to match the original form step layout.
    const stepOffset = model?.userAlreadyRegistered ? 1 : 0;
    const {t, i18n} = useTranslation();
    const { showMessage } = useSnackbarEx();
    const language = resolveApiLanguage(i18n.language);

    // The three agreement texts come from the maintainable i18n text store via
    // `GET /api/legal-documents/{document}/{language}` — anonymously reachable, so the invitation token
    // is no longer needed to read them. The German fallback for an unmaintained language is server-side.
    const [privacy, setPrivacy] = useState<AgreementState>(LOADING);
    const [confidentiality, setConfidentiality] = useState<AgreementState>(LOADING);
    const [terms, setTerms] = useState<AgreementState>(LOADING);

    useEffect(() => {
        // Responses of an outdated effect run (rapid language switch, or unmount mid-request) are
        // discarded: without the flag a late answer of the previous language could overwrite the newer
        // one, since the three requests complete independently of the order they were started in.
        let isCancelled = false;

        const loadDocument = async (
                document: LegalDocument,
                setState: React.Dispatch<React.SetStateAction<AgreementState>>,
                errorI18n: string
        ) => {
            setState(LOADING);
            const loaded = await fetchLegalDocumentContent(document, language);
            if (isCancelled) {
                return;
            }
            if (loaded) {
                setState({agreement: loaded, loadState: "loaded"});
            } else {
                // `fetchLegalDocumentContent` never throws and has already logged the cause.
                setState({loadState: "error"});
                showMessage({summary: t(errorI18n), severity: "error"});
            }
        };

        const messages = AuthI18N.login.message;
        void loadDocument(LegalDocument.PRIVACY_POLICY, setPrivacy, messages.privacyPolicyLoadFailed);
        void loadDocument(LegalDocument.CONTRACT_CONCEPT, setConfidentiality, messages.confidentialityLoadFailed);
        void loadDocument(LegalDocument.TERMS_AND_CONDITIONS, setTerms, messages.termsAndConditionsLoadFailed);

        return () => {
            isCancelled = true;
        };
    }, [language, showMessage, t]);

    const handleNext = () => {
        if (isEmpty(errors)) {
            if (activeStep === steps.length - 1) {
                // If we're at the final step and valid, trigger onSubmit if provided
                if (onSubmit) {
                    onSubmit();
                }
            } else {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
        <>
            {steps.map((step, stepIndex) => {
                const absoluteIndex = stepIndex + stepOffset;
                return (
                    <Collapse key={step} in={activeStep === stepIndex} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            {absoluteIndex === 0 && (
                                <UserSignUpForm model={model} onChange={onChange} errors={errors} />
                            )}
                            {absoluteIndex === 1 && <CompanyForm model={model} onChange={onChange} errors={errors} />}
                            {absoluteIndex === 2 && (
                                <CompanyDetailsForm model={model} onChange={onChange} errors={errors} />
                            )}
                            {absoluteIndex === 3 && (
                                <BankDetailsForm model={model} onChange={onChange} errors={errors} />
                            )}
                            {absoluteIndex === 4 && (
                                <Agreement
                                    name="privacyPolicyAccepted"
                                    title={t(AuthI18N.login.field.privacyPolicy)}
                                    agreement={privacy.agreement}
                                    loadState={privacy.loadState}
                                    value={model?.privacyPolicyAccepted}
                                    onChange={onChange}
                                    error={errors?.privacyPolicyAccepted}
                                />
                            )}
                            {absoluteIndex === 5 && (
                                <Agreement
                                    name="contractAndUsageAccepted"
                                    title={t(AuthI18N.login.field.termsAndConditions)}
                                    agreement={terms.agreement}
                                    loadState={terms.loadState}
                                    value={model?.contractAndUsageAccepted}
                                    onChange={onChange}
                                    error={errors?.contractAndUsageAccepted}
                                />
                            )}
                            {absoluteIndex === 6 && (
                                <Agreement
                                    name="confidentialityAccepted"
                                    title={t(AuthI18N.login.field.confidentialityAgreement)}
                                    agreement={confidentiality.agreement}
                                    loadState={confidentiality.loadState}
                                    value={model?.confidentialityAccepted}
                                    onChange={onChange}
                                    error={errors?.confidentialityAccepted}
                                />
                            )}
                        </Box>
                    </Collapse>
                );
            })}
            <Grid2 container direction="row" justifyContent="space-between" paddingTop={2} width="100%">
                <Grid2>
                    {activeStep !== 0 && <ButtonEx label={t(AuthI18N.login.action.back)} onClick={handleBack}/>}
                </Grid2>
                <Grid2>
                    <ButtonEx
                        label={
                            activeStep === steps.length - 1
                                    ? t(AuthI18N.login.action.finish)
                                    : t(AuthI18N.login.action.continue)
                        }
                        onClick={handleNext}
                        sx={{ ml: "auto" }}
                        disabled={!isEmpty(errors)}
                    />
                </Grid2>
            </Grid2>
        </>
    );
};
